import { useState, useEffect, useRef } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { formatCurrency } from "@/components/Layout";
import { parseDateLocal } from "@/lib/utils";
import { 
  Calendar,
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Lock,
  Sparkles,
  Shield,
  Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePaywall } from "@/components/PaywallContext";

// Status styles based on projection
const getStatusStyle = (status, isCovered) => {
  if (isCovered && status === "safe") {
    return {
      bg: "bg-emerald-50",
      expandedBg: "bg-gradient-to-br from-emerald-50 to-emerald-100/50",
      border: "border-emerald-200/50",
      textColor: "text-emerald-600",
      iconColor: "text-emerald-500",
      Icon: CheckCircle,
      badgeBg: "bg-emerald-100",
      badgeText: "text-emerald-700"
    };
  }
  if (status === "tight") {
    return {
      bg: "bg-amber-50",
      expandedBg: "bg-gradient-to-br from-amber-50 to-amber-100/50",
      border: "border-amber-200/50",
      textColor: "text-amber-600",
      iconColor: "text-amber-500",
      Icon: AlertTriangle,
      badgeBg: "bg-amber-100",
      badgeText: "text-amber-700"
    };
  }
  // Risk / projected negative
  return {
    bg: "bg-red-50",
    expandedBg: "bg-gradient-to-br from-red-50 to-red-100/50",
    border: "border-red-200/50",
    textColor: "text-red-600",
    iconColor: "text-red-500",
    Icon: AlertTriangle,
    badgeBg: "bg-red-100",
    badgeText: "text-red-700"
  };
};

// Format date for display
const formatRunOutDate = (dateStr) => {
  if (!dateStr) return null;
  const date = parseDateLocal(dateStr);
  return date.toLocaleDateString('en-GB', { 
    weekday: 'short',
    day: 'numeric', 
    month: 'short'
  });
};

export default function RunOutCard({ className = "", refreshKey = 0, startingBalance, onDataLoaded }) {
  const { user } = useAuth();
  const { openPaywall } = usePaywall();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  
  // Ref to prevent StrictMode double-fetch
  const fetchedRef = useRef(false);
  const lastKeyRef = useRef(null);

  const currency = user?.currency || 'GBP';
  const isPremium = user?.is_premium || user?.plan_type === 'premium' || user?.plan_type === 'trial';
  
  // Use prop startingBalance if provided
  const balance = startingBalance ?? user?.starting_balance ?? 0;

  useEffect(() => {
    // Create a unique key for this fetch
    const fetchKey = `${refreshKey}-${balance}`;
    
    // Skip if we already fetched with this key (handles StrictMode double-mount)
    if (fetchedRef.current && lastKeyRef.current === fetchKey) {
      return;
    }
    
    fetchedRef.current = true;
    lastKeyRef.current = fetchKey;
    fetchRunOutDate();
  }, [refreshKey, balance]);

  const fetchRunOutDate = async () => {
    try {
      const response = await axios.get(
        `${API}/dashboard/run-out-date?starting_balance=${balance}`, 
        { withCredentials: true }
      );
      setData(response.data);
      // Notify parent of loaded data (for calendar marker)
      if (onDataLoaded) {
        onDataLoaded(response.data);
      }
    } catch (error) {
      console.error("Error fetching run out date:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = () => {
    openPaywall({
      feature: "Cash Flow Forecast",
      description: "Project your finances 30 days ahead and stay in control",
      trigger_source: "cash_flow_forecast_card"
    });
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl p-3 shadow-sm border border-p2p-border/50 animate-pulse ${className}`}>
        <div className="flex items-center justify-between">
          <div className="h-4 bg-p2p-surface rounded w-20"></div>
          <div className="h-4 bg-p2p-surface rounded w-32"></div>
        </div>
      </div>
    );
  }

  // Premium locked state for free users
  if (!isPremium) {
    return (
      <div 
        className={`bg-gradient-to-br from-slate-50 to-slate-100/50 border border-slate-200/50 rounded-xl shadow-sm ${className}`}
        data-testid="run-out-card-locked"
      >
        <div className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-slate-200/50">
              <Lock className="w-3.5 h-3.5 text-slate-500" />
            </div>
            <span className="text-sm text-slate-500">Cash Flow Forecast</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUnlock}
            className="h-7 px-2.5 text-xs bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
          >
            <Sparkles className="w-3 h-3 mr-1" />
            Unlock
          </Button>
        </div>
        <div className="px-3 pb-3 pt-0">
          <p className="text-xs text-slate-400">See your 30-day cash flow projection</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { run_out_date, is_covered, status, lowest_balance, lowest_balance_date, projection_summary } = data;
  const statusStyle = getStatusStyle(status, is_covered);
  const StatusIcon = statusStyle.Icon;

  // Collapsed view - compact bar
  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className={`w-full ${statusStyle.bg} ${statusStyle.border} border rounded-xl px-4 py-3 shadow-sm ${className} transition-all hover:shadow-md`}
        data-testid="run-out-card"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${statusStyle.iconColor}`} />
            <span className="text-sm font-medium text-p2p-gray">Cash Flow Forecast</span>
          </div>
          <div className="flex items-center gap-2">
            {is_covered ? (
              <span className="text-sm text-emerald-600 font-medium">
                Looking good for 30 days
              </span>
            ) : (
              <span className={`text-sm font-medium ${statusStyle.textColor}`}>
                Shortfall around {formatRunOutDate(run_out_date)}
              </span>
            )}
            <ChevronDown className="w-4 h-4 text-p2p-muted" />
          </div>
        </div>
      </button>
    );
  }

  // Expanded view - full details
  return (
    <div 
      className={`${statusStyle.expandedBg} ${statusStyle.border} border rounded-2xl shadow-sm ${className} transition-all animate-in slide-in-from-top-1 duration-200`}
      data-testid="run-out-card"
    >
      {/* Collapsible Header */}
      <button 
        onClick={() => setExpanded(false)}
        className="w-full p-4 pb-0 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-xl ${is_covered ? 'bg-emerald-100' : 'bg-red-100'}`}>
            <Clock className={`w-5 h-5 ${is_covered ? 'text-emerald-600' : 'text-red-600'}`} />
          </div>
          <h3 className="font-semibold text-p2p-gray">Cash Flow Forecast</h3>
        </div>
        <ChevronUp className="w-5 h-5 text-p2p-muted" />
      </button>

      <div className="p-4 pt-3">
        {/* Status Message */}
        <div className="mb-4">
          {is_covered ? (
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusStyle.badgeBg} ${statusStyle.badgeText}`}>
              <Shield className="w-4 h-4" />
              You&apos;re covered for the next 30 days
            </div>
          ) : (
            <>
              <div className={`text-xl font-bold ${statusStyle.textColor} mb-1`}>
                Shortfall projected {formatRunOutDate(run_out_date)}
              </div>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusStyle.badgeBg} ${statusStyle.badgeText}`}>
                <AlertTriangle className="w-4 h-4" />
                Review recommended
              </div>
            </>
          )}
        </div>

        {/* Lowest Balance Point */}
        {!is_covered && (
          <div className="mb-4 p-3 bg-white/50 rounded-xl border border-p2p-border/30">
            <p className="text-xs text-p2p-muted mb-1">Lowest projected balance</p>
            <div className="flex items-center justify-between">
              <span className={`text-lg font-bold ${lowest_balance < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                {formatCurrency(lowest_balance, currency)}
              </span>
              <span className="text-xs text-p2p-muted">
                on {formatRunOutDate(lowest_balance_date)}
              </span>
            </div>
          </div>
        )}

        {/* Projection Breakdown */}
        {projection_summary && (
          <div className="pt-3 border-t border-p2p-border/30">
            <p className="text-xs text-p2p-muted mb-2">30-day projection</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-p2p-muted">Starting balance</span>
                <span className="text-p2p-gray font-medium">
                  {formatCurrency(projection_summary.starting_balance, currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-p2p-muted flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                  Expected income
                </span>
                <span className="text-emerald-600 font-medium">
                  +{formatCurrency(projection_summary.expected_income, currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-p2p-muted flex items-center gap-1">
                  <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                  Expected bills
                </span>
                <span className="text-red-600 font-medium">
                  -{formatCurrency(projection_summary.expected_expenses, currency)}
                </span>
              </div>
              {projection_summary.overdue_unpaid > 0 && (
                <div className="flex justify-between">
                  <span className="text-p2p-muted flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                    Overdue unpaid
                  </span>
                  <span className="text-amber-600 font-medium">
                    -{formatCurrency(projection_summary.overdue_unpaid, currency)}
                  </span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-p2p-border/30">
                <span className="text-p2p-gray font-medium">Projected balance</span>
                <span className={`font-bold ${projection_summary.final_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(projection_summary.final_balance, currency)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Export for calendar integration
export { formatRunOutDate };
